/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * An add-on's own pages, drawn from data (ADR-025 §4.6, isolation rule 5): the add-on declares
 * pages of blocks (`ui::AdminPage`, `Block`) and the Control Center draws them, so an add-on ships
 * no script. Text, a figure (Stat), a table with its filters, a form (the module settings' field
 * controls) and a button (asked to confirm when the add-on says so). Reads and actions go through
 * admin-api to the add-on worker; actions need the right to use add-ons and are recorded there.
 */

import { Play, RefreshCw, Search, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '../../api';
import type { AddonActionResult, AddonAdminPage, AddonBlock, AddonBlockData, AddonColumn, AddonState } from '../../api/areas/addons';
import type { IntegrationEnvironment } from '../../api/areas/integrations';
import type { ModuleField } from '../../api/areas/modules';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, FilterBar, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { Loading } from '../../components/States';
import { useRetained, useWrite, WriteError } from '../../components/Write';
import { formatCount, formatDate, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ChangeDialog, Panel, Stat } from '../integrations/shared';
import { checkValue, initialValue, isBlank, isRequired, payloadValue, type FormErrorCode, type FormValue } from './fieldKinds';
import { FieldInput } from './SchemaForm';
import { useText } from './ui';

type Row = Record<string, unknown>;
type Done = (r: AddonActionResult) => void;

const blockKey = (b: AddonBlock, i: number): string => ('key' in b ? `${b.kind}:${b.key}` : b.kind === 'button' ? `button:${b.action}` : `${b.kind}:${i}`);

/** A cell as its column's kind says (an unexpected value is shown as text). */
function useCell(): (column: AddonColumn, value: unknown) => ReactNode {
  const { t } = useTranslation('addonModules');
  return (column, value) => {
    if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>;
    if (typeof value === 'boolean') return value ? t('block.yes') : t('block.no');
    if (typeof value === 'object') return <code className="text-xs">{JSON.stringify(value)}</code>;
    const s = String(value);
    const n = typeof value === 'number' ? value : Number(s);
    switch (column.kind) {
      case 'number':
        return Number.isFinite(n) ? formatCount(n) : s;
      case 'vnd':
        return Number.isFinite(n) ? t('block.dong', { amount: formatCount(n) }) : s;
      case 'date':
        return formatDate(s);
      case 'date_time':
        return formatDateTime(s);
      case 'domain':
        return (
          <Link to={`/domains/${encodeURIComponent(s)}`} className="font-medium text-primary hover:underline">
            {s}
          </Link>
        );
      case 'status':
        return (
          <Badge tone="neutral" icon={false}>
            {s}
          </Badge>
        );
      case 'text':
        return s;
    }
  };
}

/** The values of a form or of a table's filters, as the add-on receives them. */
function payload(fields: readonly ModuleField[], values: Readonly<Record<string, FormValue>>): Row {
  return Object.fromEntries(fields.flatMap((f) => {
    const v = payloadValue(f, values[f.key] ?? '');
    return v === null ? [] : [[f.key, v]];
  }));
}

const initial = (fields: readonly ModuleField[]) => Object.fromEntries(fields.map((f) => [f.key, initialValue(f, null)]));

function StatBlock({ addon, block, generation }: { addon: string; block: Extract<AddonBlock, { kind: 'stat' }>; generation: number }) {
  const { t } = useTranslation('addonModules');
  const text = useText();
  const result = useApi((signal) => api.queryAddonBlock(addon, { block: block.key, params: {} }, { signal }), [addon, block.key, generation]);
  const data = useRetained(result.data);
  const shown = (d: AddonBlockData | undefined) => {
    if (!d || d.kind !== 'stat') return '—';
    return typeof d.value === 'number' ? formatCount(d.value) : d.value === null || d.value === undefined ? '—' : String(d.value);
  };
  return (
    <Stat label={text(block.label)}>
      {result.status === 'error' && !data ? (
        <span className="text-critical">{t('block.loadFailed')}</span>
      ) : (
        <span className="grid gap-0.5">
          <span className="text-2xl font-semibold tabular-nums">{shown(data)}</span>
          {data?.kind === 'stat' && data.hint ? <span className="text-xs text-muted-foreground">{data.hint}</span> : null}
        </span>
      )}
    </Stat>
  );
}

function TableBlock({ addon, block, generation, production }: { addon: string; block: Extract<AddonBlock, { kind: 'table' }>; generation: number; production: boolean }) {
  const { t } = useTranslation('addonModules');
  const text = useText();
  const cell = useCell();
  const fields = block.filters.fields;
  const [draft, setDraft] = useState<Record<string, FormValue>>(() => initial(fields));
  const [applied, setApplied] = useState<Row>({});
  const [more, setMore] = useState<{ rows: Row[]; next: string | null; key: string } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const appliedKey = JSON.stringify(applied);
  const result = useApi((signal) => api.queryAddonBlock(addon, { block: block.key, params: applied }, { signal }), [addon, block.key, appliedKey, generation]);
  const first = useRetained(result.data);
  const page = first?.kind === 'table' ? first : null;
  // Rows loaded with "Tải thêm" belong to the filters and the data they were loaded with.
  const extra = more && more.key === `${appliedKey}:${generation}` ? more : null;
  const rows = [...(page?.rows ?? []), ...(extra?.rows ?? [])];
  const next = extra ? extra.next : (page?.next ?? null);
  const errors = Object.fromEntries(fields.map((f) => [f.key, checkValue(f, draft[f.key] ?? '', production)])) as Record<string, FormErrorCode | null>;
  const valid = Object.values(errors).every((e) => !e);
  const columns: Column<Row>[] = block.columns.map((c) => ({
    key: c.key,
    header: text(c.label),
    numeric: c.kind === 'number' || c.kind === 'vnd',
    className: c.kind === 'text' ? 'whitespace-normal' : undefined,
    cell: (r) => cell(c, r[c.key]),
  }));
  const loadMore = () => {
    if (!next) return;
    setLoadingMore(true);
    setMoreError(null);
    void api
      .queryAddonBlock(addon, { block: block.key, params: { ...applied, cursor: next } })
      .then((d) => {
        if (d.kind === 'table') setMore({ rows: [...(extra?.rows ?? []), ...d.rows], next: d.next, key: `${appliedKey}:${generation}` });
      })
      .catch((e: unknown) => setMoreError(e))
      .finally(() => setLoadingMore(false));
  };
  return (
    <div className="space-y-3" data-addon-block={`table:${block.key}`}>
      {fields.length ? (
        <FilterBar
          actions={
            <>
              <Button size="sm" variant="outline" disabled={!valid} onClick={() => setApplied(payload(fields, draft))}>
                <Search /> {t('block.filter')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft(initial(fields));
                  setApplied({});
                }}
              >
                <X /> {t('block.clear')}
              </Button>
            </>
          }
        >
          {fields.map((f) => (
            <div key={f.key} className="min-w-48">
              <FieldInput field={f} value={draft[f.key] ?? ''} onChange={(v) => setDraft((x) => ({ ...x, [f.key]: v }))} required={false} error={errors[f.key] ?? null} />
            </div>
          ))}
        </FilterBar>
      ) : null}
      {result.status === 'error' && !page ? (
        <DataError error={result.error} onRetry={result.reload} title={t('block.loadFailed')} />
      ) : !page ? (
        <Loading rows={2} />
      ) : (
        <div className="rounded-lg border">
          <DataTable columns={columns} rows={rows} rowKey={(r) => JSON.stringify(r)} empty={<p className="px-5 py-4 text-sm text-muted-foreground">{t('block.empty')}</p>} />
        </div>
      )}
      {moreError ? <WriteError error={moreError} /> : null}
      {next ? (
        <Button size="sm" variant="outline" disabled={loadingMore} onClick={loadMore}>
          <RefreshCw /> {t('block.more')}
        </Button>
      ) : null}
    </div>
  );
}

function FormBlock({
  addon,
  block,
  canOperate,
  production,
  onDone,
}: {
  addon: string;
  block: Extract<AddonBlock, { kind: 'form' }>;
  canOperate: boolean;
  production: boolean;
  onDone: Done;
}) {
  const { t } = useTranslation('addonModules');
  const fields = block.schema.fields;
  const [values, setValues] = useState<Record<string, FormValue>>(() => initial(fields));
  const write = useWrite((body: { input: Row }, opts) => api.runAddonAction(addon, block.action, body, opts));
  const required = (f: ModuleField) => isRequired(f, true, values, fields);
  const errorOf = (f: ModuleField): FormErrorCode | null => {
    const v = values[f.key] ?? '';
    if (isBlank(v)) return required(f) && f.default === null ? 'REQUIRED' : null;
    return checkValue(f, v, production);
  };
  const valid = fields.every((f) => !errorOf(f));
  const submit = () =>
    void write.run({ input: payload(fields, values) }).then((r) => {
      if (!r) return;
      setValues(initial(fields));
      write.reset();
      onDone(r);
    });
  return (
    <form
      className="space-y-3 rounded-lg border p-4"
      data-addon-block={`form:${block.key}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && canOperate && !write.busy) submit();
      }}
    >
      <div className="grid items-start gap-3 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.key} className={f.kind.type === 'boolean' || f.kind.type === 'multi_select' ? 'sm:col-span-2' : undefined}>
            <FieldInput field={f} value={values[f.key] ?? ''} onChange={(v) => setValues((x) => ({ ...x, [f.key]: v }))} required={required(f)} error={errorOf(f)} />
          </div>
        ))}
      </div>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <Button type="submit" size="sm" disabled={!canOperate || !valid || write.busy}>
        <Play /> {t('block.submit')}
      </Button>
    </form>
  );
}

function ButtonBlock({ addon, block, canOperate, onDone }: { addon: string; block: Extract<AddonBlock, { kind: 'button' }>; canOperate: boolean; onDone: Done }) {
  const { t } = useTranslation('addonModules');
  const text = useText();
  const [asking, setAsking] = useState(false);
  const write = useWrite((body: { input: Row }, opts) => api.runAddonAction(addon, block.action, body, opts));
  const run = () =>
    void write.run({ input: {} }).then((r) => {
      if (!r) return;
      setAsking(false);
      write.reset();
      onDone(r);
    });
  return (
    <div className="space-y-2" data-addon-block={`button:${block.action}`}>
      <Button size="sm" variant={block.confirm ? 'destructive' : 'outline'} disabled={!canOperate || write.busy} onClick={() => (block.confirm ? setAsking(true) : run())}>
        <Play /> {text(block.label)}
      </Button>
      {write.state.phase === 'error' && !asking ? <WriteError error={write.state.error} /> : null}
      <ChangeDialog open={asking} onOpenChange={(o) => !o && setAsking(false)} title={t('block.confirmTitle')}>
        <div className="space-y-4">
          <p className="text-sm">{block.confirm ? text(block.confirm) : null}</p>
          {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAsking(false)}>
              {t('block.cancel')}
            </Button>
            <Button type="button" variant="destructive" disabled={write.busy} onClick={run}>
              {t('block.confirm')}
            </Button>
          </DialogFooter>
        </div>
      </ChangeDialog>
    </div>
  );
}

/** One page: its blocks in order; an action's message is shown, and the page's data is read again when the add-on asks. */
export function AddonPageView({ addon, page, canOperate, env }: { addon: string; page: AddonAdminPage; canOperate: boolean; env: IntegrationEnvironment }) {
  const { t } = useTranslation('addonModules');
  const text = useText();
  const [generation, setGeneration] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const production = env === 'PRODUCTION';
  const done: Done = (r) => {
    setMessage(r.message);
    if (r.reload) setGeneration((n) => n + 1);
  };
  const acts = page.blocks.some((b) => b.kind === 'form' || b.kind === 'button');
  const stats = page.blocks.filter((b): b is Extract<AddonBlock, { kind: 'stat' }> => b.kind === 'stat');
  return (
    <div className="space-y-4" data-addon-page={page.key}>
      {acts && !canOperate ? <Callout tone="info">{t('pages.readOnly')}</Callout> : null}
      {message ? <Callout tone="ok">{message}</Callout> : null}
      {page.blocks.map((b, i) => {
        switch (b.kind) {
          case 'text':
            return (
              <p key={blockKey(b, i)} className="text-sm whitespace-pre-line">
                {text(b.body)}
              </p>
            );
          case 'stat':
            // Figures side by side, where the first of them stands.
            return stats[0] === b ? (
              <div key={blockKey(b, i)} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {stats.map((s) => (
                  <StatBlock key={s.key} addon={addon} block={s} generation={generation} />
                ))}
              </div>
            ) : null;
          case 'table':
            return <TableBlock key={blockKey(b, i)} addon={addon} block={b} generation={generation} production={production} />;
          case 'form':
            return <FormBlock key={blockKey(b, i)} addon={addon} block={b} canOperate={canOperate} production={production} onDone={done} />;
          case 'button':
            return <ButtonBlock key={blockKey(b, i)} addon={addon} block={b} canOperate={canOperate} onDone={done} />;
        }
      })}
    </div>
  );
}

/**
 * The pages of a running add-on, as the add-on worker describes them (only those the caller may
 * open). `?page=` names the one shown (a menu entry of the navigation opens its page), else the first.
 */
export function AddonPages({ addon, state, env }: { addon: string; state: AddonState; env: IntegrationEnvironment }) {
  const { t } = useTranslation('addonModules');
  const text = useText();
  const running = state === 'ACTIVE';
  const result = useApi((signal) => (running ? api.getAddonPages(addon, { signal }) : Promise.resolve(null)), [addon, running]);
  const [params, setParams] = useSearchParams();
  const selected = params.get('page');
  const setSelected = (key: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', key);
        return next;
      },
      { replace: true },
    );
  if (!running) {
    return (
      <Panel title={t('pages.title')}>
        <p className="text-sm text-muted-foreground">{t('pages.notRunning')}</p>
      </Panel>
    );
  }
  if (result.status === 'error') {
    return (
      <Panel title={t('pages.title')}>
        <DataError error={result.error} onRetry={result.reload} title={t('pages.loadFailed')} />
      </Panel>
    );
  }
  const data = result.data;
  if (!data) {
    return (
      <Panel title={t('pages.title')}>
        <Loading rows={3} />
      </Panel>
    );
  }
  if (!data.pages.length) {
    return (
      <Panel title={t('pages.title')}>
        <p className="text-sm text-muted-foreground">{t('pages.none')}</p>
      </Panel>
    );
  }
  const page = data.pages.find((p) => p.key === selected) ?? data.pages[0];
  if (!page) return null;
  const entry = data.menu.find((m) => m.page === page.key);
  return (
    <Panel
      title={text(page.title)}
      description={entry ? t('pages.section', { section: t(`section.${entry.section}`) }) : undefined}
      actions={
        data.pages.length > 1 ? (
          <span className="flex flex-wrap gap-1" role="tablist" aria-label={t('pages.title')}>
            {data.pages.map((p) => (
              <Button key={p.key} size="xs" role="tab" aria-selected={p.key === page.key} variant={p.key === page.key ? 'default' : 'outline'} onClick={() => setSelected(p.key)}>
                {text(p.title)}
              </Button>
            ))}
          </span>
        ) : null
      }
    >
      <AddonPageView key={page.key} addon={addon} page={page} canOperate={data.can_operate} env={env} />
    </Panel>
  );
}
